<template>
  <v-autocomplete
    v-model="localValue"
    :style="cssVars"
    :data-testid="dataTestid"
    :density="density"
    :items="sortedItems"
    :hide-details="hideDetails"
    :disabled="disabled"
    :single-line="singleLine"
    :item-title="itemTitle"
    :item-value="itemValue"
    :multiple="multiple"
    :chips="multiple"
    :closable-chips="closableChips"
    :clearable="clearable"
    :error="error"
    :messages="messages"
    :open-on-focus="true"
    @update:menu="onMenuChange"
  >
    <template #prepend-item>
      <div class="fake-prepend-item"></div>
    </template>

    <template v-for="(_, slot) of $slots as AutocompleteSlots" #[slot]="slotData">
      <slot :name="slot" v-bind="slotData" />
    </template>
  </v-autocomplete>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

import type { AutocompleteSlots } from '@/types/vuetify.d';
import type { Density, Item, ModelValue } from '@/types/common.d';
import { useSort } from '@/composables/useSort';

type Props = {
  density?: Density;
  items: Item[];
  itemValue?: string;
  // Accept Vuetify's item-title function signature (item => string)
  itemTitle?: string | ((item: Item) => string);
  error?: boolean;
  messages?: string;
  modelValue?: ModelValue;
  hideDetails?: boolean;
  singleLine?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  chips?: boolean;
  closableChips?: boolean;
  clearable?: boolean;
  dataTestid?: string;
  width?: number;
};

const emit = defineEmits<{
  (e: 'update:modelValue', v: ModelValue): void;
  (e: 'change', v: ModelValue): void;
  (e: 'menu-change', open: boolean): void;
  (e: 'update:menu', open: boolean): void;
}>();

const props = withDefaults(defineProps<Props>(), {
  density: 'default',
  itemValue: 'value',
  itemTitle: 'title',
  modelValue: null,
  multiple: false,
  chips: true,
  closableChips: false,
  clearable: true,
  dataTestid: undefined,
  messages: '',
  width: undefined,
});

const { sortAlphabetically } = useSort();

const sortedItems = computed<Item[]>(() => sortAlphabetically(props.items, props.itemTitle));

const localValue = computed({
  get: () => props.modelValue,
  set: (localValue) => {
    emit('update:modelValue', localValue);
    emit('change', localValue);
  },
});

// Emit custom event instead of accepting a callback prop
const onMenuChange = (isOpen: boolean) => {
  emit('update:menu', isOpen);
  emit('menu-change', isOpen);
};

const cssVars = computed(() => ({
  '--input-width': `${props.width}px`,
}));
</script>

<style lang="scss" scoped>
.v-list-item {
  min-width: 250px;
}
.fake-prepend-item {
  height: 1px;
}
::v-deep(.v-field) {
  width: var(--input-width);
}
</style>
